import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { fetchCurrentProfile } from "../../../api/profile";
import { BookACall } from "../../welcomeSetup/elements/bookACall";

export const ScheduleCallModal = ({ open, onClose, profile, setProfile }) => {
  const [view, setView] = useState("intro");
  const [loading, setLoading] = useState(false);
  const bookACallRef = useRef();
  useEffect(() => { if (open) setView("intro"); }, [open]);

  const handleClose = () => {
    onClose();
  };

  const handleSchedule = async () => {
    setLoading(true);
    try {
      await bookACallRef.current?.submitBooking?.();
      const [currentProfile] = await fetchCurrentProfile();
      setProfile(currentProfile);
      setView("success");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          p: 0,
          maxWidth:
            view === "intro" ? "700px" : view === "success" ? "650px" : "800px",
          textAlign: "center",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* ================= BOOKING VIEW ================= */}
        {view === "booking" && (
          <Stack sx={{ textAlign: "left" }}>
            <Typography variant="h6" fontWeight={600} sx={{ p: "16px 24px" }}>
              Schedule Intro Call
            </Typography>

            <Divider />

            <BookACall
              profile={profile}
              ref={bookACallRef}
            />

            <Divider />

            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "stretch", sm: "flex-end" },
                gap: 2,
                p: "16px 20px",
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setView("intro")}
                sx={{
                  borderRadius: "8px",
                  minHeight: "42px",
                  textTransform: "none",
                  fontWeight: 500,
                  width: { xs: "100%", sm: "auto" },
                  order: { xs: 2, sm: 1 },
                }}
              >
                Back
              </Button>

              <Button
                variant="contained"
                onClick={handleSchedule}
                disabled={loading}
                sx={{
                  borderRadius: "8px",
                  minHeight: "42px",
                  textTransform: "none",
                  fontWeight: 500,
                  background: "#536DEC",
                  boxShadow: "0 4px 14px rgba(91,108,255,0.35)",
                  "&:hover": {
                    background: "#4C63D7",
                  },
                  width: { xs: "100%", sm: "94px" },
                  order: { xs: 1, sm: 2 },
                }}
              >
                {loading ? "Scheduling..." : "Schedule"}
              </Button>
            </Box>
          </Stack>
        )}

        {/* ================= SUCCESS VIEW ================= */}
        {view === "success" && (
          <Box sx={{ p: "48px 36px" }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Box
                component="img"
                src="/Success Animation.gif"
                alt="Setup Complete"
                sx={{
                  width: { xs: "120px", sm: "160px" },
                  height: "auto",
                }}
              />
            </Box>

            <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
              You’re Booked! 🎉
            </Typography>

            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              One of our team members will meet with you at the scheduled time
              to walk through the platform and discuss setting up your
              personalized dashboard.
            </Typography>

            <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
              You’ll receive a confirmation email shortly with all the details.
            </Typography>

            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                borderRadius: "8px",
                maxWidth: "260px",
                minHeight: "42px",
                width: "100%",
                textTransform: "none",
                fontWeight: 500,
                background: "#536DEC",
                boxShadow: "0 4px 14px rgba(91,108,255,0.35)",
                "&:hover": {
                  background: "#4C63D7",
                },
              }}
            >
              Return To Demo Dashboard
            </Button>
          </Box>
        )}

        {/* ================= INTRO VIEW ================= */}
        {view === "intro" && (
          <Box sx={{ p: "40px 36px" }}>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
              Want Help Setting This Up For Your Business?
            </Typography>

            <Box
              component="img"
              src="/support-illustration.svg"
              alt="Support illustration"
              sx={{
                width: "100%",
                maxWidth: 356,
                mx: "auto",
                mb: 3,
                display: "block",
              }}
            />

            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              You’re currently viewing a demo dashboard with sample data.
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                maxWidth: 520,
                mx: "auto",
                mb: 4,
              }}
            >
              If you’d like, one of our team members can walk you through the
              platform, answer questions, and help you get set up with a
              personalized dashboard.
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  borderRadius: "8px",
                  maxWidth: "242px",
                  minHeight: "42px",
                  width: "100%",
                  px: 3,
                  textTransform: "none",
                  fontWeight: 500,
                  color: "#536DEC",
                  borderColor: "#536DEC"
                }}
              >
                Return To Demo Dashboard
              </Button>

              <Button
                variant="contained"
                onClick={() => setView("booking")}
                sx={{
                  borderRadius: "8px",
                  maxWidth: "242px",
                  minHeight: "42px",
                  width: "100%",
                  px: 3,
                  textTransform: "none",
                  fontWeight: 500,
                  background: "#536DEC",
                  boxShadow: "0 4px 14px rgba(91,108,255,0.35)",
                  "&:hover": {
                    background: "#4C63D7",
                  },
                }}
              >
                Book Free Strategy Call
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
